import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createVNode as _createVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, renderList as _renderList, Fragment as _Fragment, unref as _unref, createTextVNode as _createTextVNode } from "vue"

const _hoisted_1 = { class: "text-h6 font-weight-bold" }
const _hoisted_2 = {
  key: 1,
  class: "grid grid-cols-1 md:grid-cols-2 gap-6"
}
const _hoisted_3 = { class: "border border-dashed border-gray-300 rounded px-3 pt-2 pb-3 mb-4" }
const _hoisted_4 = {
  key: 0,
  class: "text-xs text-error mb-2",
  "data-cy": "disb-send-to-diverges"
}
const _hoisted_5 = { class: "grid grid-cols-2 gap-4" }
const _hoisted_6 = {
  class: "border border-gray-200 rounded bg-white p-5",
  "data-cy": "disb-send-preview"
}
const _hoisted_7 = { class: "font-bold mb-3" }
const _hoisted_8 = { class: "text-sm mb-3" }
const _hoisted_9 = {
  class: "inline-block bg-green-regular text-white text-sm font-semibold rounded px-4 py-2 mt-1 mb-3",
  "data-cy": "disb-send-preview-cta"
}
const _hoisted_10 = { class: "text-xs text-action-grey" }
const _hoisted_11 = {
  key: 2,
  class: "text-sm text-error mt-4",
  "data-cy": "disb-send-amount-drift"
}
const _hoisted_12 = {
  key: 3,
  class: "text-sm text-error mt-4",
  "data-cy": "disb-send-error"
}

import { computed, reactive, ref, watch } from 'vue';
import LlTextField from '@/components/LlTextField.vue';
import LlTextarea from '@/components/LlTextarea.vue';
import LlSecondaryButton from '@/components/LlSecondaryButton.vue';
import Loader from '@/components/Loader.vue';
import { formatCurrency, isValidEmail, isValidSingleEmail } from '@/services/utils';
import useClientMatterStore from '@/services/useClientMatterStore';
import type {
  DisbursementEmailComposition,
  SendClientDisbursementPayload,
} from '@/models/disbursementTypes';
import {
  DISBURSEMENT_CTA_LABEL,
  LINK_EXPIRY_DAYS,
  buildDefaultBody,
  buildDefaultSubject,
  buildGreeting,
  formatContactAddress,
  fromEmailHtml,
  hasHeaderInjectionRisk,
  normalizeRecipients,
  toEmailHtml,
} from '@/utils/payabliDisbursementEmail';

const SUBJECT_MAX = 255;


export default /*@__PURE__*/_defineComponent({
  __name: 'SendDepositRequestDialog',
  props: {
    modelValue: { type: Boolean },
    matterId: {},
    clientName: {},
    amount: {},
    sending: { type: Boolean },
    errorMessage: {},
    resendFrom: {}
  },
  emits: ["update:modelValue", "submit"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const ctaLabel = DISBURSEMENT_CTA_LABEL;
const expiryDays = LINK_EXPIRY_DAYS;

const clientMatterStore = useClientMatterStore();
const formRef = ref<{ validate: () => Promise<{ valid: boolean }> } | null>(null);
const loadingContact = ref(false);
const attempted = ref(false);
const onFileEmail = ref('');
const onFileEmailFromMatter = ref(false);
const composedAmount = ref(0);
const form = reactive({
  clientName: '',
  mailingAddress: '',
  to: '',
  ccs: '',
  bccs: '',
  subject: '',
  body: '',
});

const requiredRule = (message: string) => (value: string): true | string =>
  !!value?.trim() || message;

const toRule = (value: string): true | string =>
  !value?.trim() || isValidSingleEmail(value.trim()) || 'Enter a valid email address.';

const optionalEmailsRule = (value: string): true | string =>
  !value?.trim() || isValidEmail(normalizeRecipients(value)) || 'Enter valid, comma-separated email addresses.';

const subjectRule = (value: string): true | string => {
  if (hasHeaderInjectionRisk(value ?? '')) return 'The subject cannot contain line breaks.';
  return (value ?? '').length <= SUBJECT_MAX || `Keep the subject under ${SUBJECT_MAX} characters.`;
};

// Mirrors the :rules above for environments without v-form, the same way LienFormPanel does.
const canSend = computed(
  () =>
    !props.sending
    && !loadingContact.value
    && !!form.clientName.trim()
    && !!form.body.trim()
    && requiredRule('')(form.subject) === true
    && subjectRule(form.subject) === true
    && !!form.to.trim()
    && toRule(form.to) === true
    && optionalEmailsRule(form.ccs) === true
    && optionalEmailsRule(form.bccs) === true,
);

const onFileEmailSource = computed(() =>
  (onFileEmailFromMatter.value
    ? 'the contact recorded on the matter'
    : "the client's contact email"),
);

const toDiverges = computed(
  () =>
    !!onFileEmail.value
    && !!form.to.trim()
    && toRule(form.to) === true
    && form.to.trim().toLowerCase() !== onFileEmail.value.toLowerCase(),
);

// A resend carries the amount already sent; the live net-recovery figure is irrelevant to it.
const amountDrifted = computed(
  () => !props.resendFrom && !loadingContact.value && props.amount !== composedAmount.value,
);

const greeting = computed(() => buildGreeting(form.clientName || props.clientName));
const previewParagraphs = computed(() =>
  form.body.split(/\n\s*\n/).map((block) => block.trim()).filter(Boolean),
);

/**
 * Mirrors the backend's BR-011 resolution (Matter.Email ?? Client.Email) so the address shown
 * here is the same one the Send gate checked — a different answer would let the modal open
 * pre-filled with an address the gate never approved.
 */
function applyContact(matter: any): void {
  const matterContact = matter?.contact ?? {};
  const clientContact = matter?.client?.contact ?? {};

  // ClientService regenerates client.name as DisplayName -> CompanyName -> "Last, First" on every
  // save, so it already IS the precedence chain — re-deriving it here would be a second copy that
  // drifts. It is also what invoice and trust-request emails greet the client by ({client_name}),
  // so this keeps one client addressed the same way across every email LeanLaw sends.
  form.clientName = matter?.client?.name || props.clientName;
  form.mailingAddress = formatContactAddress(clientContact.address);
  if (matter?.name) form.subject = buildDefaultSubject(matter.name);

  // Whichever contact wins supplies the cc/bcc too. Taking the matter's address but the client's
  // cc would copy someone the matter-level contact was written to exclude — and this email
  // carries the client's settlement figures.
  onFileEmailFromMatter.value = !!matterContact.email;
  const source = onFileEmailFromMatter.value ? matterContact : clientContact;
  onFileEmail.value = source.email || '';
  form.to = onFileEmail.value;
  form.ccs = normalizeRecipients(source.cc || '');
  form.bccs = normalizeRecipients(source.bcc || '');
}

function applyResendFrom(previous: DisbursementEmailComposition): void {
  attempted.value = false;
  composedAmount.value = props.amount;
  onFileEmail.value = '';
  onFileEmailFromMatter.value = false;
  form.clientName = previous.clientName || props.clientName;
  form.mailingAddress = previous.mailingAddress;
  form.to = previous.to;
  form.ccs = normalizeRecipients(previous.ccs);
  form.bccs = normalizeRecipients(previous.bccs);
  form.subject = previous.subject;
  form.body = fromEmailHtml(previous.body);
}

async function loadRecipient(): Promise<void> {
  if (props.resendFrom) {
    applyResendFrom(props.resendFrom);
    return;
  }
  loadingContact.value = true;
  attempted.value = false;
  composedAmount.value = props.amount;
  onFileEmail.value = '';
  onFileEmailFromMatter.value = false;
  form.clientName = props.clientName;
  form.mailingAddress = '';
  form.to = '';
  form.ccs = '';
  form.bccs = '';
  // Filled in properly from the matter once it loads; the settlement read carries no name.
  form.subject = buildDefaultSubject('');
  form.body = buildDefaultBody(props.amount);

  try {
    // force: the contact is the one thing this modal must not serve from an earlier page's cache.
    applyContact(await clientMatterStore.getMatterById(props.matterId, true));
  } catch {
    // The defaults above still stand and every field is editable, so a failed read costs the
    // firm the pre-fill, not the send.
  } finally {
    loadingContact.value = false;
  }
}

watch(
  () => props.modelValue,
  (open) => {
    if (open) loadRecipient();
  },
  { immediate: true },
);

function close(): void {
  emit('update:modelValue', false);
}

async function confirm(): Promise<void> {
  if (!canSend.value) return;
  attempted.value = true;
  try {
    const result = await formRef.value?.validate?.();
    if (result && !result.valid) return;
  } catch {
    // canSend already mirrors every rule above, so a v-form that fails to run is not itself a
    // reason to refuse a composition that has otherwise validated.
  }

  emit('submit', {
    clientName: form.clientName.trim(),
    mailingAddress: form.mailingAddress.trim(),
    to: form.to.trim(),
    ccs: normalizeRecipients(form.ccs),
    bccs: normalizeRecipients(form.bccs),
    subject: form.subject.trim(),
    body: toEmailHtml(form.body),
  });
}

return (_ctx: any,_cache: any) => {
  const _component_v_card_title = _resolveComponent("v-card-title")!
  const _component_v_form = _resolveComponent("v-form")!
  const _component_v_card_text = _resolveComponent("v-card-text")!
  const _component_v_card_actions = _resolveComponent("v-card-actions")!
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_dialog = _resolveComponent("v-dialog")!

  return (_openBlock(), _createBlock(_component_v_dialog, {
    "model-value": __props.modelValue,
    "max-width": "960",
    persistent: "",
    "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => (_ctx.$emit('update:modelValue', $event)))
  }, {
    default: _withCtx(() => [
      _createVNode(_component_v_card, {
        rounded: "lg",
        "data-cy": "disb-send-dialog"
      }, {
        default: _withCtx(() => [
          _createVNode(_component_v_card_title, { class: "d-flex align-center justify-space-between pt-5 px-6" }, {
            default: _withCtx(() => [
              _createElementVNode("span", _hoisted_1, _toDisplayString(__props.resendFrom ? 'Resend Deposit Request Email' : 'Send Deposit Request'), 1),
              _createVNode(LlSecondaryButton, {
                icon: "mdi-close",
                variant: "text",
                size: "small",
                "aria-label": "Close",
                disabled: __props.sending,
                "data-cy": "disb-send-close",
                onClick: close
              }, null, 8, ["disabled"])
            ]),
            _: 1
          }),
          _createVNode(_component_v_card_text, { class: "px-6 pb-2" }, {
            default: _withCtx(() => [
              (loadingContact.value)
                ? (_openBlock(), _createBlock(Loader, {
                    key: 0,
                    what: 'recipient',
                    class: "my-12"
                  }))
                : (_openBlock(), _createElementBlock("div", _hoisted_2, [
                    _createVNode(_component_v_form, {
                      ref_key: "formRef",
                      ref: formRef
                    }, {
                      default: _withCtx(() => [
                        _createElementVNode("fieldset", _hoisted_3, [
                          _cache[8] || (_cache[8] = _createElementVNode("legend", { class: "text-xs uppercase tracking-wider text-action-grey px-1" }, " Recipient on file — editable ", -1)),
                          _createVNode(LlTextField, {
                            modelValue: form.clientName,
                            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((form.clientName) = $event)),
                            label: "Full name",
                            rules: [requiredRule('A recipient name is required.')],
                            "data-cy": "disb-send-name"
                          }, null, 8, ["modelValue", "rules"]),
                          _createVNode(LlTextarea, {
                            modelValue: form.mailingAddress,
                            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((form.mailingAddress) = $event)),
                            label: "Mailing address",
                            rows: 3,
                            "data-cy": "disb-send-address"
                          }, null, 8, ["modelValue"])
                        ]),
                        _createVNode(LlTextField, {
                          modelValue: form.to,
                          "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((form.to) = $event)),
                          label: "To",
                          rules: [requiredRule('A recipient email address is required.'), toRule],
                          "data-cy": "disb-send-to"
                        }, null, 8, ["modelValue", "rules"]),
                        (toDiverges.value)
                          ? (_openBlock(), _createElementBlock("div", _hoisted_4, " This differs from " + _toDisplayString(onFileEmailSource.value) + " (" + _toDisplayString(onFileEmail.value) + "). The deposit link will go to the address above, for this send only — the client record is unchanged. ", 1))
                          : _createCommentVNode("", true),
                        _createElementVNode("div", _hoisted_5, [
                          _createElementVNode("div", null, [
                            _createVNode(LlTextField, {
                              modelValue: form.ccs,
                              "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((form.ccs) = $event)),
                              label: "Cc",
                              rules: [optionalEmailsRule],
                              "data-cy": "disb-send-cc"
                            }, null, 8, ["modelValue", "rules"])
                          ]),
                          _createElementVNode("div", null, [
                            _createVNode(LlTextField, {
                              modelValue: form.bccs,
                              "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((form.bccs) = $event)),
                              label: "Bcc",
                              rules: [optionalEmailsRule],
                              "data-cy": "disb-send-bcc"
                            }, null, 8, ["modelValue", "rules"])
                          ])
                        ]),
                        _createVNode(LlTextField, {
                          modelValue: form.subject,
                          "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((form.subject) = $event)),
                          label: "Subject",
                          rules: [requiredRule('A subject is required.'), subjectRule],
                          "data-cy": "disb-send-subject"
                        }, null, 8, ["modelValue", "rules"]),
                        _createVNode(LlTextarea, {
                          modelValue: form.body,
                          "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((form.body) = $event)),
                          label: "Message",
                          rows: 5,
                          rules: [requiredRule('A message is required.')],
                          "data-cy": "disb-send-body"
                        }, null, 8, ["modelValue", "rules"])
                      ]),
                      _: 1
                    }, 512),
                    _createElementVNode("div", null, [
                      _cache[9] || (_cache[9] = _createElementVNode("div", { class: "text-xs uppercase tracking-wider text-action-grey mb-2" }, "Preview", -1)),
                      _createElementVNode("div", _hoisted_6, [
                        _createElementVNode("div", _hoisted_7, _toDisplayString(form.subject), 1),
                        _createElementVNode("p", _hoisted_8, _toDisplayString(greeting.value), 1),
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(previewParagraphs.value, (paragraph, index) => {
                          return (_openBlock(), _createElementBlock("p", {
                            key: index,
                            class: "text-sm mb-3 whitespace-pre-line"
                          }, _toDisplayString(paragraph), 1))
                        }), 128)),
                        _createElementVNode("div", _hoisted_9, _toDisplayString(_unref(ctaLabel)), 1),
                        _createElementVNode("div", _hoisted_10, " Link expires in " + _toDisplayString(_unref(expiryDays)) + " days · sent to " + _toDisplayString(form.to || '—'), 1)
                      ])
                    ])
                  ])),
              (amountDrifted.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_11, " The client's net recovery changed to " + _toDisplayString(_unref(formatCurrency)(__props.amount)) + " while this was open. Update the message before sending — the payment uses the new figure either way. ", 1))
                : _createCommentVNode("", true),
              (attempted.value && __props.errorMessage && !loadingContact.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_12, _toDisplayString(__props.errorMessage), 1))
                : _createCommentVNode("", true)
            ]),
            _: 1
          }),
          _createVNode(_component_v_card_actions, { class: "px-6 pb-5 pt-2 justify-end gap-2" }, {
            default: _withCtx(() => [
              _createVNode(LlSecondaryButton, {
                disabled: __props.sending,
                "data-cy": "disb-send-cancel",
                onClick: close
              }, {
                default: _withCtx(() => [...(_cache[10] || (_cache[10] = [
                  _createTextVNode(" Cancel ", -1)
                ]))]),
                _: 1
              }, 8, ["disabled"]),
              _createVNode(LlSecondaryButton, {
                color: "primary",
                variant: "elevated",
                loading: __props.sending,
                disabled: !canSend.value,
                "data-cy": "disb-send-confirm",
                onClick: confirm
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(__props.resendFrom ? 'Resend' : 'Send'), 1)
                ]),
                _: 1
              }, 8, ["loading", "disabled"])
            ]),
            _: 1
          })
        ]),
        _: 1
      })
    ]),
    _: 1
  }, 8, ["model-value"]))
}
}

})